/**
 * Passwort vergessen (Phase C, Kapitel 3.3, Entscheidung #7) — unabhängig
 * vom Login erreichbar. Zeigt IMMER dieselbe generische Erfolgsmeldung,
 * egal ob die E-Mail existiert (User-Enumeration-Schutz, siehe Backend).
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        body: { email: email.trim() },
      });
      hapticSuccess();
      setDone(true);
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Anfrage fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-sm mx-auto mt-12">
      <h2 className="text-2xl font-bold text-center mb-2">Passwort vergessen</h2>
      <p className="text-sm text-slate-400 text-center mb-6">
        Wir schicken dir einen Link zum Zurücksetzen deines Passworts.
      </p>
      {done ? (
        <div className="space-y-4">
          <p className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2">
            Falls diese E-Mail-Adresse registriert ist, wurde gerade ein Reset-Link
            verschickt. Schau auch im Spam-Ordner nach.
          </p>
          <Link to="/login" className="block text-center text-brand-light hover:text-brand underline">
            Zurück zum Login
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm text-slate-300 mb-1">
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
            />
          </div>
          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-brand hover:bg-brand-dark transition rounded py-2 font-medium disabled:opacity-50"
          >
            {busy ? 'Sende…' : 'Link anfordern'}
          </button>
          <p className="text-sm text-center">
            <Link to="/login" className="text-brand-light hover:text-brand !min-h-0 underline">
              Zurück zum Login
            </Link>
          </p>
        </form>
      )}
    </div>
  );
}
